import { useState, useCallback } from 'react';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';
import { getHostsApi } from '@/services/bolApiClient';
import { Host, PaginatedHosts } from '@/api/bol';

export interface UseHostsReturn {
  hosts: Host[];
  loading: boolean;
  selectedHost: Host | null;
  setSelectedHost: (host: Host | null) => void;
  fetchHosts: (page?: number, pageSize?: number) => Promise<void>;
  createHost: (hostData: { name: string; uri: string }) => Promise<void>;
  deleteHost: (hostName: string) => Promise<void>;
  setTotal: (total: number) => void;
  total: number;
}

export const useHosts = (): UseHostsReturn => {
  const [hosts, setHosts] = useState<Host[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedHost, setSelectedHost] = useState<Host | null>(null);
  const [total, setTotal] = useState(0);
  
  const { showSuccess, showError } = useSnackbarNotification();

  const fetchHosts = useCallback(async (page: number = 0, pageSize: number = 10) => {
    console.log('Fetching hosts with:', { page, pageSize });

    setLoading(true);
    
    try {
      const api = getHostsApi();
      
      const response: PaginatedHosts = await api.listHosts({
        limit: pageSize,
        offset: page * pageSize
      });
      
      console.log('Hosts API response:', response);
      
      setHosts(response.results || []);
      setTotal(response.pagination?.total || 0);
    } catch (err: unknown) {
      console.error('Error fetching hosts:', err);
      const errorMessage = err instanceof Error
        ? err.message || 'Failed to fetch hosts'
        : 'Failed to fetch hosts';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const createHost = useCallback(async (hostData: { name: string; uri: string }) => {
    try {
      console.log('Creating host with data:', hostData);
      
      const api = getHostsApi();
      
      await api.createHost({
        contentType: 'application/json',
        host: hostData,
        source: true
      });
      
      showSuccess('Host created successfully');
    } catch (err: unknown) {
      console.error('Error creating host:', err);
      const errorMessage = err instanceof Error
        ? err.message || 'Failed to create host'
        : 'Failed to create host';
      showError(`Create host failed: ${errorMessage}`);
      throw err; // Re-throw to let the calling component handle form state
    }
  }, [showSuccess, showError]);

  const deleteHost = useCallback(async (hostName: string) => {
    try {
      const api = getHostsApi();
      
      await api.deleteHostById({
        hostId: hostName
      });
      
      showSuccess('Host deleted successfully');
    } catch (err: unknown) {
      console.error('Error deleting host:', err);
      const errorMessage = err instanceof Error
        ? err.message || 'Failed to delete host'
        : 'Failed to delete host';
      showError(`Delete host failed: ${errorMessage}`);
      throw err; // Re-throw to let the calling component handle dialog state
    }
  }, [showSuccess, showError]);

  return {
    hosts,
    loading,
    selectedHost,
    setSelectedHost,
    fetchHosts,
    createHost,
    deleteHost,
    setTotal,
    total
  };
};